import { useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChatContextType } from '@trinity/chat';
import { useLocation } from '@trinity/chat-components/containers/Chat/RoutingProvider';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient';
import { useActiveApplicationIdentifier } from '@trinity/chat-components/containers/UserApplications';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import {
  ActiveChatSessionContext,
  ChatLandingCardsVisibleContext,
  ChatLandingCardsContext,
  ChatLandingCardsPromptsContext,
  ChatLandingCardsLoadingContext,
  ChatSessionItemsContext,
} from './context';

type Props = {
  children: React.ReactNode;
};

export const LandingCardsContainer = (props: Props) => {
  const brandIdentifier = useActiveBrandIdentifier();
  const applicationIdentifier = useActiveApplicationIdentifier();
  // This is the initial visibility from the parent ChatStateContainer (chat state), which is then overridden by the local provider below.
  const landingCardsVisibleCtx = useContext(ChatLandingCardsVisibleContext);
  const chat = useContext(ActiveChatSessionContext);
  const items = useContext(ChatSessionItemsContext);
  const location = useLocation();
  const apiClient = useChatApiClient();

  const showLandingCards = useMemo(() => {
    if (items.length) {
      return false;
    }

    // Don't show landing cards when the chat is embedded inside a scoped
    // surface: workspaces, projects, segments, or the segmentation builder
    // (SegmentProfile's Synthetic Audience tab lives at /segmentation/segment/:id).
    if (
      location.pathname.includes('/workspaces') ||
      location.pathname.includes('/projects') ||
      location.pathname.includes('/project') ||
      location.pathname.includes('/segments') ||
      location.pathname.includes('/segmentation')
    ) {
      return false;
    }

    // Don't show landing cards if chat context type is Document
    if (chat.context?.type === ChatContextType.Document) {
      return false;
    }

    return !!landingCardsVisibleCtx;
  }, [landingCardsVisibleCtx, location.pathname, chat.context?.type, items.length]);

  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const query = useQuery({
    queryKey: ['landing-cards', { brandIdentifier, applicationIdentifier, showLandingCards }],
    enabled: !!brandIdentifier && !!applicationIdentifier && showLandingCards,
    queryFn: async () => {
      if (!brandIdentifier || !applicationIdentifier) {
        return {
          cards: [],
          prompts: [],
        };
      }
      const response = await apiClient.chat.getChatLandingCards({
        brandIdentifier: brandIdentifier,
        applicationIdentifier: applicationIdentifier,
      });
      return {
        cards: response.cards,
        prompts: response.prompts,
      };
    },
    placeholderData: {
      cards: [],
      prompts: [],
    },
  });

  return (
    <ChatLandingCardsLoadingContext.Provider value={query.isLoading}>
      <ChatLandingCardsContext.Provider value={query.data?.cards ?? []}>
        <ChatLandingCardsVisibleContext.Provider
          value={showLandingCards && !!query.data?.cards && query.data.cards.length > 0}
        >
          <ChatLandingCardsPromptsContext.Provider value={query.data?.prompts ?? []}>
            {props.children}
          </ChatLandingCardsPromptsContext.Provider>
        </ChatLandingCardsVisibleContext.Provider>
      </ChatLandingCardsContext.Provider>
    </ChatLandingCardsLoadingContext.Provider>
  );
};
